/* eslint-disable @typescript-eslint/no-explicit-any */
// Onboarding wizard for a freshly-checked-out site.
// 7 steps. State persists on every change via websites.onboarding_state.
// Step 7 reads resolve_model_for_call for each task so the user sees the
// resolved model before activating.

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Sparkles, Zap, Trophy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const tenantDb = supabase as any;

const TASK_TYPES = ["rewrite", "image", "seo", "qa", "score", "translate", "embed"] as const;
type TaskType = (typeof TASK_TYPES)[number];

interface OnboardingState {
  current_step?: number;
  completed_steps?: number[];
  data?: Record<string, any>;
}

interface SiteRow {
  id: string;
  name: string;
  company_id: string;
  domain: string | null;
  template_id: string | null;
  onboarding_state: OnboardingState | null;
  onboarding_completed_at: string | null;
  brand_voice: any;
  status: string;
  publish_cadence: string | null;
  primary_language: string | null;
}

type Preset = "balanced" | "quality" | "speed";
const PRESETS: Record<Preset, Partial<Record<TaskType, string>>> = {
  balanced: {
    rewrite: "claude-haiku-4-5-rewrite",
    image: "flux-schnell",
    seo: "gemini-2-5-flash-seo",
    qa: "claude-sonnet-4-6-qa",
    score: "claude-haiku-4-5-score",
  },
  quality: {
    rewrite: "claude-opus-4-7-rewrite",
    image: "flux-ultra-1-1",
    seo: "claude-haiku-4-5-seo",
    qa: "claude-opus-4-7-qa",
    score: "claude-haiku-4-5-score",
  },
  speed: {
    rewrite: "gemini-2-5-flash-rewrite",
    image: "imagen-4-fast",
    seo: "gemini-2-5-flash-seo",
    qa: "gpt-5-qa",
    score: "gemini-2-5-flash-score",
  },
};

export default function OnboardingWizard() {
  const { site_id } = useParams<{ site_id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: site, isLoading } = useQuery<SiteRow | null>({
    queryKey: ["onboarding-site", site_id],
    queryFn: async () => {
      const { data } = await tenantDb
        .from("websites")
        .select("id, name, company_id, domain, template_id, onboarding_state, onboarding_completed_at, brand_voice, status, publish_cadence, primary_language")
        .eq("id", site_id!)
        .maybeSingle();
      return data;
    },
    enabled: !!site_id,
  });

  const [step, setStep] = useState(1);
  const [state, setState] = useState<OnboardingState>({});

  useEffect(() => {
    if (site?.onboarding_state) {
      setState(site.onboarding_state);
      setStep(site.onboarding_state.current_step ?? 1);
    }
  }, [site?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveState = useMutation({
    mutationFn: async (next: OnboardingState) => {
      const { error } = await tenantDb
        .from("websites")
        .update({ onboarding_state: next })
        .eq("id", site_id!);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding-site", site_id] }),
  });

  const goTo = (newStep: number) => {
    const next: OnboardingState = {
      ...state,
      current_step: newStep,
      completed_steps: Array.from(new Set([...(state.completed_steps ?? []), step])),
    };
    setState(next);
    setStep(newStep);
    saveState.mutate(next);
  };

  const setData = (k: string, v: any) => {
    const next: OnboardingState = { ...state, data: { ...(state.data ?? {}), [k]: v } };
    setState(next);
    saveState.mutate(next);
  };

  if (isLoading || !site) return <FullScreenSpinner />;

  if (site.onboarding_completed_at) {
    return (
      <CompletedView siteName={site.name} onOpen={() => navigate(`/websites/${site.id}`)} />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/40 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-base font-semibold">{site.name}</h1>
            <p className="text-xs text-muted-foreground">{t("onboarding.title", "Onboarding")} · {step} / 7</p>
          </div>
          <ProgressBar step={step} />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8 space-y-6">
        {step === 1 && <Step1Domain site={site} data={state.data ?? {}} setData={setData} onNext={() => goTo(2)} />}
        {step === 2 && <Step2Template site={site} data={state.data ?? {}} setData={setData} onBack={() => goTo(1)} onNext={() => goTo(3)} />}
        {step === 3 && <Step3CategorySources site={site} data={state.data ?? {}} setData={setData} onBack={() => goTo(2)} onNext={() => goTo(4)} />}
        {step === 4 && <Step4BrandVoice site={site} data={state.data ?? {}} setData={setData} onBack={() => goTo(3)} onNext={() => goTo(5)} />}
        {step === 5 && <Step5ModelPrefs site={site} data={state.data ?? {}} setData={setData} onBack={() => goTo(4)} onNext={() => goTo(6)} />}
        {step === 6 && <Step6Schedule site={site} data={state.data ?? {}} setData={setData} onBack={() => goTo(5)} onNext={() => goTo(7)} />}
        {step === 7 && <Step7Review site={site} data={state.data ?? {}} onBack={() => goTo(6)} onActivate={async () => {
          await tenantDb.from("websites").update({
            status: "active",
            onboarding_completed_at: new Date().toISOString(),
            // Schedule the first pipeline run immediately.
            next_run_at: new Date(Date.now() + 60_000).toISOString(),
          }).eq("id", site.id);
          toast.success(t("onboarding.activatedToast", "Site activated"));
          navigate(`/websites/${site.id}`);
        }} />}
      </main>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Step components

function Step1Domain({ site, data, setData, onNext }: StepProps & { onNext: () => void }) {
  const [domain, setDomain] = useState(data.domain ?? site.domain ?? "");
  return (
    <Card>
      <CardHeader><CardTitle>1. Domain</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <Label htmlFor="domain">Your site's domain</Label>
        <Input
          id="domain"
          placeholder="news.example.com"
          value={domain}
          onChange={(e) => { setDomain(e.target.value); setData("domain", e.target.value); }}
        />
        <p className="text-xs text-muted-foreground">
          You can register a new domain via our reseller, or point an existing one. DNS instructions are emailed after activation.
        </p>
        <NavBar onNext={onNext} canNext={domain.trim().length > 3} />
      </CardContent>
    </Card>
  );
}

function Step2Template({ site, data, setData, onBack, onNext }: StepProps & { onBack: () => void; onNext: () => void }) {
  const { data: templates = [] } = useQuery({
    queryKey: ["onboarding-templates"],
    queryFn: async () => {
      const { data } = await tenantDb.from("site_templates").select("id, name, description, preview_url").eq("active", true).order("sort_order").limit(20);
      return data ?? [];
    },
  });
  const selected = data.template_id ?? site.template_id ?? null;
  return (
    <Card>
      <CardHeader><CardTitle>2. Template</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(templates as any[]).map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => setData("template_id", tpl.id)}
              className={`rounded-lg border p-4 text-left transition-colors ${selected === tpl.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent/30"}`}
            >
              <p className="text-sm font-medium">{tpl.name}</p>
              <p className="text-xs text-muted-foreground line-clamp-2">{tpl.description}</p>
            </button>
          ))}
        </div>
        {templates.length === 0 && <p className="text-sm text-muted-foreground">No templates available yet — your tier may not include extra templates.</p>}
        <NavBar onBack={onBack} onNext={onNext} canNext={!!selected} />
      </CardContent>
    </Card>
  );
}

function Step3CategorySources({ data, setData, onBack, onNext }: StepProps & { onBack: () => void; onNext: () => void }) {
  const [category, setCategory] = useState(data.category ?? "");
  const [sources, setSources] = useState<string[]>(data.sources ?? []);
  const [newSource, setNewSource] = useState("");
  return (
    <Card>
      <CardHeader><CardTitle>3. Category and sources</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="category">Primary category</Label>
          <Input id="category" value={category} onChange={(e) => { setCategory(e.target.value); setData("category", e.target.value); }} placeholder="Technology, Finance, Sports…" />
        </div>
        <div className="space-y-2">
          <Label>News sources (RSS feeds or news site URLs)</Label>
          <div className="flex gap-2">
            <Input placeholder="https://example.com/feed.xml" value={newSource} onChange={(e) => setNewSource(e.target.value)} />
            <Button variant="outline" onClick={() => {
              if (!newSource.trim()) return;
              const next = [...sources, newSource.trim()];
              setSources(next);
              setData("sources", next);
              setNewSource("");
            }}>Add</Button>
          </div>
          <ul className="space-y-1 text-sm">
            {sources.map((src, i) => (
              <li key={i} className="flex items-center justify-between rounded bg-muted/40 px-3 py-1.5">
                <span className="truncate">{src}</span>
                <button onClick={() => { const next = sources.filter((_, j) => j !== i); setSources(next); setData("sources", next); }} className="text-xs text-destructive">remove</button>
              </li>
            ))}
          </ul>
        </div>
        <NavBar onBack={onBack} onNext={onNext} canNext={category.trim().length > 1 && sources.length >= 1} />
      </CardContent>
    </Card>
  );
}

function Step4BrandVoice({ site, data, setData, onBack, onNext }: StepProps & { onBack: () => void; onNext: () => void }) {
  const [samples, setSamples] = useState<string[]>(data.brand_voice_samples ?? ["", "", ""]);
  const allFilled = samples.filter((s) => s.trim().length > 100).length >= 3;
  return (
    <Card>
      <CardHeader><CardTitle>4. Brand voice</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Paste 3 to 5 sample articles from your existing publication. We'll learn your tone, vocabulary, and structure. Min 100 chars each.</p>
        {samples.map((s, i) => (
          <div key={i} className="space-y-1">
            <Label>Sample {i + 1}</Label>
            <Textarea
              value={s}
              onChange={(e) => { const next = [...samples]; next[i] = e.target.value; setSamples(next); setData("brand_voice_samples", next); }}
              rows={5}
              className="text-sm"
            />
          </div>
        ))}
        {samples.length < 5 && (
          <Button variant="outline" size="sm" onClick={() => setSamples([...samples, ""])}>Add another sample</Button>
        )}
        <NavBar onBack={onBack} onNext={async () => {
          // Store raw samples + a basic style fingerprint. Full AI distillation
          // happens server-side in the brand-voice-extract edge fn (future).
          await tenantDb.from("websites").update({
            brand_voice: {
              samples: samples.filter((s) => s.trim()),
              captured_at: new Date().toISOString(),
            },
          }).eq("id", site.id);
          onNext();
        }} canNext={allFilled} />
      </CardContent>
    </Card>
  );
}

function Step5ModelPrefs({ site, data, setData, onBack, onNext }: StepProps & { onBack: () => void; onNext: () => void }) {
  const [chosenPreset, setChosenPreset] = useState<Preset | "custom" | null>(data.model_preset ?? null);

  const applyPreset = async (preset: Preset) => {
    setChosenPreset(preset);
    setData("model_preset", preset);
    // Wipe existing then insert new website_ai_preferences rows.
    await tenantDb.from("website_ai_preferences").delete().eq("website_id", site.id);
    const rows = Object.entries(PRESETS[preset]).map(([task_type, preferred_model_key]) => ({
      website_id: site.id,
      task_type,
      preferred_model_key,
    }));
    if (rows.length > 0) {
      await tenantDb.from("website_ai_preferences").insert(rows);
    }
  };

  const skip = async () => {
    setChosenPreset("custom");
    setData("model_preset", "skipped");
    await tenantDb.from("website_ai_preferences").delete().eq("website_id", site.id);
    onNext();
  };

  return (
    <Card>
      <CardHeader><CardTitle>5. AI model preferences</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Pick a preset, customise per task, or skip — your organisation's defaults apply.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <PresetCard
            label="Balanced"
            description="Recommended for most newsrooms"
            icon={<Sparkles className="h-4 w-4" />}
            selected={chosenPreset === "balanced"}
            onClick={() => applyPreset("balanced")}
          />
          <PresetCard
            label="Quality first"
            description="Best models — higher credit cost"
            icon={<Trophy className="h-4 w-4" />}
            selected={chosenPreset === "quality"}
            onClick={() => applyPreset("quality")}
          />
          <PresetCard
            label="Speed and cost first"
            description="Cheapest, fastest models"
            icon={<Zap className="h-4 w-4" />}
            selected={chosenPreset === "speed"}
            onClick={() => applyPreset("speed")}
          />
        </div>
        <div className="flex items-center justify-between rounded-md border border-dashed border-border p-3">
          <p className="text-xs text-muted-foreground">Want fine-grained control? Customise each task type after onboarding in <span className="font-medium">Site settings → AI models</span>.</p>
          <Button size="sm" variant="ghost" onClick={skip}>Skip — use org defaults</Button>
        </div>
        <NavBar onBack={onBack} onNext={onNext} canNext={chosenPreset !== null} />
      </CardContent>
    </Card>
  );
}

function Step6Schedule({ site, data, setData, onBack, onNext }: StepProps & { onBack: () => void; onNext: () => void }) {
  const [cadence, setCadence] = useState<string>(data.publish_cadence ?? site.publish_cadence ?? "daily");
  return (
    <Card>
      <CardHeader><CardTitle>6. Publication schedule</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(["manual", "daily", "hourly", "realtime"] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => { setCadence(opt); setData("publish_cadence", opt); }}
              className={`rounded-lg border p-3 text-sm capitalize transition-colors ${cadence === opt ? "border-primary bg-primary/5" : "border-border hover:bg-accent/30"}`}
            >
              {opt}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Higher cadences require Network tier or above. We'll silently downgrade if your tier doesn't allow the choice.</p>
        <NavBar onBack={onBack} onNext={async () => {
          await tenantDb.from("websites").update({ publish_cadence: cadence }).eq("id", site.id);
          onNext();
        }} canNext={true} />
      </CardContent>
    </Card>
  );
}

function Step7Review({ site, data, onBack, onActivate }: Omit<StepProps, "setData"> & { onBack: () => void; onActivate: () => Promise<void> }) {
  const [resolved, setResolved] = useState<Partial<Record<TaskType, any>>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const out: Partial<Record<TaskType, any>> = {};
      for (const task of TASK_TYPES) {
        const { data: row } = await tenantDb.rpc("resolve_model_for_call", {
          p_company_id: site.company_id,
          p_website_id: site.id,
          p_task_type: task,
          p_requested_model_key: null,
        });
        out[task] = Array.isArray(row) ? row[0] : row;
      }
      setResolved(out);
      setLoading(false);
    })();
  }, [site.id, site.company_id]);

  return (
    <Card>
      <CardHeader><CardTitle>7. Review and activate</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Field label="Domain" value={data.domain ?? site.domain ?? "—"} />
          <Field label="Category" value={data.category ?? "—"} />
          <Field label="Sources" value={`${(data.sources ?? []).length} configured`} />
          <Field label="Brand voice" value={`${(data.brand_voice_samples ?? []).filter((s: string) => s).length} samples`} />
          <Field label="Schedule" value={data.publish_cadence ?? "daily"} />
          <Field label="Model preset" value={data.model_preset ?? "skipped"} />
        </div>
        <div>
          <h3 className="text-sm font-semibold mt-2 mb-2">Resolved models</h3>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <ul className="space-y-1 text-xs">
              {TASK_TYPES.map((t) => (
                <li key={t} className="flex items-center justify-between rounded bg-muted/30 px-3 py-1.5">
                  <span className="capitalize">{t}</span>
                  <span className="flex items-center gap-2">
                    <code className="text-muted-foreground">{resolved[t]?.model_key ?? "—"}</code>
                    <Badge variant="outline" className="text-[9px]">{resolved[t]?.resolved_via ?? "—"}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <NavBar onBack={onBack} onNext={onActivate} canNext={true} nextLabel="Activate site" nextIcon={<CheckCircle2 className="ml-2 h-4 w-4" />} />
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Tiny helper components

interface StepProps {
  site: SiteRow;
  data: Record<string, any>;
  setData: (k: string, v: any) => void;
}

function NavBar({ onBack, onNext, canNext = true, nextLabel = "Continue", nextIcon }: {
  onBack?: () => void; onNext: () => void; canNext?: boolean; nextLabel?: string; nextIcon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between pt-2">
      {onBack ? <Button variant="ghost" onClick={onBack}><ChevronLeft className="mr-2 h-4 w-4" />Back</Button> : <span />}
      <Button onClick={onNext} disabled={!canNext}>
        {nextLabel}{nextIcon ?? <ChevronRight className="ml-2 h-4 w-4" />}
      </Button>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded border border-border bg-card p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium">{String(value)}</p>
    </div>
  );
}

function PresetCard({ label, description, icon, selected, onClick }: {
  label: string; description: string; icon: React.ReactNode; selected: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-accent/30"}`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">{icon}<span>{label}</span></div>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </button>
  );
}

function ProgressBar({ step }: { step: number }) {
  return (
    <div className="flex w-32 gap-1">
      {[1, 2, 3, 4, 5, 6, 7].map((s) => (
        <div key={s} className={`h-1 flex-1 rounded ${s <= step ? "bg-primary" : "bg-muted"}`} />
      ))}
    </div>
  );
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function CompletedView({ siteName, onOpen }: { siteName: string; onOpen: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
          <h2 className="text-lg font-semibold">{siteName} is ready</h2>
          <p className="text-sm text-muted-foreground">Onboarding completed. The first article batch will run shortly.</p>
          <Button onClick={onOpen} className="w-full">Open site</Button>
        </CardContent>
      </Card>
    </div>
  );
}
